<template>
  <div class="table-container">
    <table v-if="logs.length > 0">
      <thead>
      <tr>
        <th>Data/Hora</th>
        <th>Evento</th>
        <th>Status</th>
        <th class="actions-col">Ações</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="log in logs" :key="log.id" :class="getRowClass(log)">
        <td>
          <div class="datetime-cell">
            <span class="date">{{ formatDate(log.received_at) }}</span>
            <span class="time">{{ formatTime(log.received_at) }}</span>
          </div>
        </td>
        
        <td>
          <span :class="['event-badge', getBadgeClass(log.mp_topic)]">
            {{ log.mp_topic || 'UNKNOWN' }}
          </span>
        </td>
        
        <td>
          <div class="status-cell" :class="getStatusColorClass(log)">
            <span class="material-icons-outlined icon-sm">{{ getStatusIcon(log) }}</span>
            <span class="status-text">{{ getCleanStatusLabel(log) }}</span>
          </div>
        </td>
        
        <td class="actions-col">
          <button @click="openModal(log)" class="action-btn details" title="Ver Detalhes Completos">
            <span class="material-icons-outlined">visibility</span>
          </button>
        </td>
      </tr>
      </tbody>
    </table>
    
    <div v-else class="empty-state">
      <span class="material-icons-outlined empty-icon">hub</span>
      <p>Nenhum log registrado.</p>
    </div>
    
    <BaseModal v-if="selectedLog" @close="closeModal">
      
      <template v-slot:title>
        <div class="custom-modal-title">
          <span>Webhook #{{ selectedLog.id }}</span>
          <span :class="['event-badge', getBadgeClass(selectedLog.mp_topic)]">
            {{ selectedLog.mp_topic }}
          </span>
        </div>
      </template>
      
      <div class="webhook-details-container">
        <div class="detail-group">
          <label>Mensagem do Processamento</label>
          <div class="status-box" :class="selectedLog.processing_status">
            <span class="material-icons-outlined">{{ getStatusIcon(selectedLog) }}</span>
            <span>{{ selectedLog.processing_details || 'Sem detalhes registrados.' }}</span>
          </div>
        </div>
        
        <div class="detail-row">
          <div class="detail-group half">
            <label>ID no Mercado Pago</label>
            <span class="info-text">{{ selectedLog.mp_id || '-' }}</span>
          </div>
          <div class="detail-group half">
            <label>Recebido em</label>
            <span class="info-text">
              {{ formatDate(selectedLog.received_at) }} às {{ formatTime(selectedLog.received_at) }}
            </span>
          </div>
        </div>
        
        <div class="detail-group">
          <label>Payload JSON Original</label>
          <div class="json-container">
            <pre>{{ parseJsonPretty(selectedLog.raw_payload) }}</pre>
          </div>
        </div>
      </div>
      
      <template v-slot:actions>
        <button @click="closeModal" class="modal-btn-no">Fechar</button>
      </template>
    
    </BaseModal>
  
  </div>
</template>

<script>
import BaseModal from './BaseModal.vue';

export default {
  name: "AdminWebhooks",
  components: { BaseModal },
  props: {
    logs: { type: Array, default: () => [] }
  },
  data() {
    return {
      selectedLog: null
    };
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('pt-BR');
    },
    formatTime(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },
    
    // --- Modal ---
    openModal(log) { this.selectedLog = log; },
    closeModal() { this.selectedLog = null; },
    
    // --- Lógica de Status Simplificado ---
    getCleanStatusLabel(log) {
      const statusDb = log.processing_status;
      const msg = (log.processing_details || '').toLowerCase();
      
      if (statusDb === 'pending') return 'Processando...';
      if (statusDb === 'error') return 'Erro';
      if (statusDb === 'ignored') return 'Ignorado';
      
      if (statusDb === 'success') {
        if (msg.includes('approved')) return 'Aprovado';
        if (msg.includes('pending')) return 'Pendente';
        if (msg.includes('rejected')) return 'Rejeitado';
        if (msg.includes('cancelled')) return 'Cancelado';
        if (msg.includes('estoque')) return 'Devolvido';
        return 'Sincronizado';
      }
      
      return statusDb;
    },
    
    getStatusIcon(log) {
      const label = this.getCleanStatusLabel(log).toLowerCase();
      const statusDb = log.processing_status;
      
      if (statusDb === 'error') return 'warning';
      if (statusDb === 'pending') return 'hourglass_empty';
      if (statusDb === 'ignored') return 'filter_alt_off';
      
      if (label === 'aprovado') return 'check_circle';
      if (label === 'pendente') return 'schedule';
      if (label === 'rejeitado' || label === 'cancelado') return 'cancel';
      if (label === 'devolvido') return 'assignment_return';
      
      return 'sync_alt';
    },
    
    // --- Classes CSS ---
    getBadgeClass(topic) {
      if (!topic) return 'badge-other';
      if (topic.includes('payment')) return 'badge-payment';
      if (topic.includes('merchant_order')) return 'badge-order';
      return 'badge-other';
    },
    
    getStatusColorClass(log) {
      const statusDb = log.processing_status;
      if (statusDb === 'error') return 'status-error';
      if (statusDb === 'pending') return 'status-pending';
      if (statusDb === 'ignored') return 'status-ignored';
      
      const label = this.getCleanStatusLabel(log).toLowerCase();
      if (label === 'aprovado') return 'status-success';
      if (label === 'pendente') return 'status-pending';
      if (label === 'rejeitado' || label === 'cancelado') return 'status-warning';
      
      return 'status-synced';
    },
    
    getRowClass(log) {
      if (log.processing_status === 'error') return 'row-error';
      return '';
    },
    
    // --- Helpers JSON ---
    parseJsonPretty(raw) {
      try {
        const obj = (typeof raw === 'string') ? JSON.parse(raw) : raw;
        return JSON.stringify(obj, null, 2);
      } catch { return raw; }
    }
  }
};
</script>

<style scoped src="@/assets/admin-styles.css"></style>
<style scoped>
.datetime-cell { display: flex; flex-direction: column; font-size: 0.85rem; }
.time { color: var(--text-secondary); font-size: 0.75rem; }

/* Badges de Evento */
.event-badge {
  padding: 4px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
}
.badge-payment { background-color: #E3F2FD; color: #1565C0; border: 1px solid #BBDEFB; }
.badge-order { background-color: #F5F5F5; color: #616161; border: 1px solid #E0E0E0; }
.badge-other { background-color: #FFF3E0; color: #EF6C00; border: 1px solid #FFE0B2; }

/* Célula de Status (Tabela) */
.status-cell { display: flex; align-items: center; gap: 8px; font-size: 0.9rem; font-weight: 500; }
.status-text { white-space: nowrap; }

.status-success { color: #2E7D32; }
.status-pending { color: #F57C00; }
.status-warning { color: #D32F2F; }
.status-ignored { color: #9E9E9E; font-style: italic; font-weight: 400; }
.status-synced { color: #1976D2; }
.status-error { color: #B00020; font-weight: 700; }

.actions-col { text-align: center; }
.icon-sm { font-size: 20px; }

/* --- ESTILOS INTERNOS DO MODAL --- */

/* Título Personalizado no Header */
.custom-modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

/* Container Interno (Substitui .webhook-modal antigo) */
.webhook-details-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-group label {
  font-size: 0.8rem; font-weight: 700; color: var(--text-secondary);
  text-transform: uppercase; margin-bottom: 5px; display: block;
}

/* Caixa de Status no Modal (Destaque) */
.status-box {
  padding: 15px; border-radius: 6px;
  display: flex; align-items: center; gap: 10px;
  font-weight: 600; font-size: 1rem;
  border: 1px solid currentColor;
  background-color: rgba(255,255,255,0.03);
}
.status-box.success { color: #2E7D32; border-color: #A5D6A7; }
.status-box.ignored { color: #757575; border-color: #E0E0E0; }
.status-box.error { color: #C62828; border-color: #EF9A9A; }
.status-box.pending { color: #F57C00; border-color: #FFCC80; }

.detail-row { display: flex; gap: 20px; }
.detail-group.half { flex: 1; }

.info-text {
  display: block; padding: 10px;
  background: var(--bg-main); border: 1px solid var(--border-light);
  border-radius: 4px; font-family: monospace; font-size: 1rem;
  color: var(--text-main);
}

.json-container {
  background-color: #1e1e1e; color: #e0e0e0;
  padding: 15px; border-radius: 6px;
  font-family: 'Consolas', monospace; font-size: 0.85rem;
  /* Overflow é gerenciado pelo BaseModal, mas o JSON precisa de scroll horizontal se for largo */
  overflow-x: auto;
  border: 1px solid #333;
}

/* Mobile Responsivo */
@media (max-width: 600px) {
  .detail-row { flex-direction: column; gap: 15px; }
}
</style>